import { HttpStatusCode } from "axios";
import { useApplicationContext } from "../../context/ApplicationContext";
import {
  ModulePath,
  Settings_Module,
  Severity,
  VerificationType,
} from "../../constant/ApplicationVariables";
import { useLocation, useNavigate } from "react-router-dom";
import { useRef, useCallback } from "react";

/**
 * Custom hook for handling HTTP error responses.
 * @returns {function} errorHandler - Function to handle HTTP error responses.
 */
export default function useErrorHandler() {
  // Accessing necessary context for displaying global snackbar messages
  const {
    setGLobalSnackBar,
    showTokenExpired,
    setShowTokenExpired,
    userDetail,
    showLicenseDialog,
    setShowLicenseDialog,
    activeSettingsModule,
    setActiveSettingsModule,
  } = useApplicationContext();
  const navigate = useNavigate();
  const pathName = useLocation()?.pathname;
  
  // Add a ref to track processed requests and prevent infinite loops
  const processedRequests = useRef(new Map());
  const timeoutRef = useRef(null);

  /**
   * Handles HTTP error responses based on status codes and triggers appropriate actions.
   * @param {object} response - HTTP response object containing status and data.
   * @param {function} setLoader - Optional function to set loader state.
   * @param {function} setOpen - Optional function to set open state for dialogs.
   * @param {function} handleClose - Optional function to handle closing actions.
   */
  const errorHandler = useCallback((response, setLoader, setOpen, handleClose) => {
    // CRITICAL FIX: Only handle errors, ignore successful responses
    if (!response?.status || response.status === HttpStatusCode.Ok || response.status === HttpStatusCode.Created) {
      console.log("Skipping error handler for successful response:", response?.status);
      
      // Still close loader for successful responses
      if (setLoader) {
        setTimeout(() => setLoader(false), 0);
      }
      if (setOpen) {
        setOpen(false);
      }
      if (handleClose) {
        handleClose();
      }
      return; // Exit early for successful responses
    }

    // Create a unique key for this error to prevent duplicate processing
    const errorKey = `${response?.status}-${response?.config?.url}-${JSON.stringify(response?.data)}`;
    
    // Check if this exact error was already processed recently
    if (processedRequests.current.has(errorKey)) {
      const lastProcessed = processedRequests.current.get(errorKey);
      if (Date.now() - lastProcessed < 1000) { // Ignore if processed within last second
        console.log("Skipping duplicate error processing:", errorKey);
        return;
      }
    }
    
    // Mark this error as processed
    processedRequests.current.set(errorKey, Date.now());
    
    // Clean up old entries periodically
    if (processedRequests.current.size > 100) {
      const now = Date.now();
      for (const [key, timestamp] of processedRequests.current.entries()) {
        if (now - timestamp > 5000) { // Remove entries older than 5 seconds
          processedRequests.current.delete(key);
        }
      }
    }

    console.log(response, response?.status, setLoader, "customHook"); // Logging the response and custom hook message

    // Clear any pending timeout to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Handling different HTTP status codes with corresponding severity levels
    if (response?.message === "Network Error") {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (!response?.status) {
      // When failed to connect with api / api failed to send response
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.ProxyAuthenticationRequired) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
      setShowTokenExpired(true);
    } else if (response.status === HttpStatusCode.PreconditionFailed) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);

      timeoutRef.current = setTimeout(() => {
        navigate(ModulePath.resetPassword, {
          state: {
            email: userDetail?.email,
            type: VerificationType.ForgotPassword,
            fromPath: pathName,
          },
        });
      }, 100);
    } else if (response.status === HttpStatusCode.Forbidden) {
      // license dialog
      setGLobalSnackBar({
        open: true,
        title:
          "You don't have an active license plan. Please purchase a license to continue using the application.",
        severity: Severity.warning,
      });

      setActiveSettingsModule(Settings_Module.license);
      timeoutRef.current = setTimeout(() => {
        // setShowLicenseDialog(true);
        navigate(ModulePath.settings);
      }, 100);
    } else if (response.status === HttpStatusCode.Unauthorized) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.NotFound) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.BadRequest) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.BadGateway) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.TooManyRequests) {
      handleSnackbar(
        response,
        Severity.warning,
        setLoader,
        setOpen,
        handleClose,
      );
    } else if (response.status === HttpStatusCode.Conflict) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.InternalServerError) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.GatewayTimeout) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.NotAcceptable) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.ServiceUnavailable) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    } else if (response.status === HttpStatusCode.UpgradeRequired) {
      handleSnackbar(response, Severity.error, setLoader, setOpen, handleClose);
    }
  }, [setGLobalSnackBar, setShowTokenExpired, userDetail, navigate, pathName, setActiveSettingsModule]);

  /**
   * Displays a global snackbar message based on the severity and performs optional actions.
   * @param {object} response - HTTP response object containing status and data.
   * @param {string} severity - Severity level for the snackbar (success, error, warning).
   * @param {function} setLoader - Optional function to set loader state.
   * @param {function} setOpen - Optional function to set open state for dialogs.
   * @param {function} handleClose - Optional function to handle closing actions.
   */
  const handleSnackbar = useCallback((
    response,
    severity,
    setLoader,
    setOpen,
    handleClose,
  ) => {
    // Prevent showing snackbar for successful responses
    if (response?.status === HttpStatusCode.Ok || response?.status === HttpStatusCode.Created) {
      console.log("Skipping snackbar for successful response");
      // Still close loader/dialogs
      if (setLoader) setTimeout(() => setLoader(false), 0);
      if (setOpen) setOpen(false);
      if (handleClose) handleClose();
      return;
    }
    
    // Setting global snackbar with message title and severity
    setGLobalSnackBar({
      open: true,
      title: !response?.status
        ? `Failed to connect with server`
        : response?.response?.data?.detail
          ? response?.response?.data?.detail
          : response?.response?.data?.message &&
              response?.response?.data?.fields &&
              Object.keys(response?.response?.data?.fields)?.length > 0
            ? response?.response?.data?.message +
              " " +
              JSON.stringify(response?.response?.data?.fields)
            : response?.data?.message &&
                response?.data?.message &&
                response?.data?.fields &&
                Object.keys(response?.data?.fields)?.length > 0
              ? response?.data?.message +
                " " +
                JSON.stringify(response?.data?.fields)
              : (response?.response?.data?.message ??
                response?.data?.message ??
                response?.data?.messages ??
                response?.message ??
                (typeof response?.data === "string" && response?.data)),
      severity: severity,
    });

    // Performing optional actions based on provided functions
    if (setLoader) setTimeout(() => setLoader(false), 0); // Close loader if setLoader function is provided
    if (setOpen) setOpen(false); // Close dialog if setOpen function is provided
    if (handleClose) handleClose(); // Execute handleClose function if provided
  }, [setGLobalSnackBar]);

  // Cleanup on unmount
  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    processedRequests.current.clear();
  };

  // Return cleanup with the error handler
  errorHandler.cleanup = cleanup;
  
  return errorHandler; // Returning the errorHandler function for handling HTTP errors
}